import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { buildValidations, validator } from '@eflexsystems/ember-tracked-validations';
import { compact, sortByProp } from 'ramda-adjunct';

@buildValidations({
  customer: validator('presence', true),
  project: validator('presence', true),
})
class LegacyLicense extends Model {
  @attr('string') licenseId;
  @attr('string') customer;
  @attr('string') project;
  @attr('string') jobNumber;
  @attr('string') greenKey;
  @attr('boolean', { defaultValue: false }) isVirtual;
  @attr('string') notes;
  @attr('date', { defaultValue() { return new Date(); } }) createdAt;
  @attr('date', { defaultValue() { return new Date(); } }) updatedAt;

  @hasMany('changelog', { async: false, cascadeDelete: true, inverse: null }) changelogs;

  @belongsTo('analytics', { async: false, inverse: null, embedded: true, singularize: false }) analytics;
  @belongsTo('assembly', { async: false, inverse: null, embedded: true }) assembly;
  @belongsTo('evision', { async: false, inverse: null, embedded: true }) evision;
  @belongsTo('jel', { async: false, inverse: null, embedded: true }) jel;
  @belongsTo('jem', { async: false, inverse: null, embedded: true }) jem;
  @belongsTo('kitting', { async: false, inverse: null, embedded: true }) kitting;
  @belongsTo('oee', { async: false, inverse: null, embedded: true }) oee;
  @belongsTo('quality', { async: false, inverse: null, embedded: true }) quality;
  @belongsTo('track-and-trace', { async: false, inverse: null, embedded: true }) trackAndTrace;

  get isDirty() {
    return super.isDirty ||
      this.changelogs.some(item => item.isDirty) ||
      this.products.some(item => item.isDirty);
  }

  get hasProductExpirationWarning() {
    return this.products.some(product => product.expirationWarning);
  }

  get hasExpiredProduct() {
    return this.products.some(product => product.expired);
  }

  get products() {
    return compact([
      this.analytics,
      this.assembly,
      this.evision,
      this.jel,
      this.jem,
      this.kitting,
      this.oee,
      this.quality,
      this.trackAndTrace,
    ]);
  }

  get sortedChangelogs() {
    return sortByProp('createdAt', this.changelogs ?? []);
  }

  async save(options) {
    await super.save(options);

    this.changelogs.forEach(changelog => {
      if (!changelog.isDirty) {
        return;
      }

      changelog.save();
    });
  }

  rollbackAttributes() {
    this.changelogs.forEach(changelog => {
      if (changelog.isDirty) {
        changelog.rollbackAttributes();
      }
    });

    this.products.forEach(product => {
      if (product.isDirty) {
        product.rollbackAttributes();
      }
    });

    super.rollbackAttributes(...arguments);
  }
}

export default LegacyLicense;
